import NavigationService from '@disco/state-management/NavigationService'
import type { ItemWithUpdatedFields } from 'types/nesting'
import type { Thunk } from 'types/redux'
import type { NestingLabel, Track, TrackId } from 'types/track'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { updatePanes } from '@disco/state-management/actions/ExplorerActions'
import {
  getOrFetchTrack,
  loadBatch,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import { normalizeNestedTracksInShortlists } from '@disco/state-management/actions/ShortlistActions'
import api from '@disco/api/server'
import {
  NESTING_LOAD,
  NESTING_LOAD_ERROR,
  NESTING_TRACKS_LOAD_SUCCESS,
  NESTING_UPDATING,
  NESTING_UPDATING_ERROR,
  NESTING_UPDATING_SUCCESS,
} from '@disco/constants/ActionTypes'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import {
  ALREADY_NESTED_TRACK,
  NESTED_INTO_ITSELF,
  NESTED_TRACK_INTO_ANOTHER_TRACK,
  STEM,
  STEMS_LIMIT,
  TOO_MANY_VERSIONS,
  VERSIONS_LIMIT,
} from '@disco/constants/Nesting'
import { getExplorerPanesByTrackId } from '@disco/state-management/selectors/explorer'
import { getNestedTracksIdsForFetch } from '@disco/state-management/selectors/nesting'
import {
  getTrack,
  getTracksByNestingParentId,
} from '@disco/state-management/selectors/tracks'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { addItems } from '@disco/utils/immutable'
import {
  getStemsFromTracksArray,
  getVersionsFromTracksArray,
  shiftPosition,
} from '@disco/utils/nesting'
import { pushUINotification } from '@disco/utils/notifications'
import { plural } from '@disco/utils/string-utils'
import {
  getNestedTracksCount,
  getStemsCount,
  getStemsCountFromTracksArray,
  getVersionsCount,
  getVersionsCountFromTracksArray,
} from '@disco/utils/tracks'
import { ExplorerPane } from '@disco/types/types/explorer'
/**
 * Fetches nested tracks from the server using a URL and track IDs.
 * @param params - Object containing URL and track IDs
 * @param params.url - The API URL to fetch nested tracks from
 * @param params.mainTrackIds - Array of main track IDs to fetch nested tracks for
 * @returns Thunk that fetches and loads nested tracks into the store
 */
export function fetchNestedTracksByUrl({
  url,
  mainTrackIds,
}: {
  url: string | null | undefined
  mainTrackIds: TrackId[]
}): Thunk {
  return async (dispatch, getState) => {
    if (!url || mainTrackIds.length === 0) return

    try {
      let state = getState()
      const trackIds = getNestedTracksIdsForFetch(state, mainTrackIds)
      if (trackIds.length === 0) return
      mainTrackIds.forEach(id => {
        dispatch({
          type: NESTING_LOAD,
          id,
        })
      })
      const loadedNestedTracks = await api.postUrl<Track[]>(
        url,
        {
          track_ids: trackIds,
        },
        'api.fetchNestedTracksByUrl',
        {
          skipClientIdentification: true,
        },
      )

      if (loadedNestedTracks.length !== 0) {
        dispatch(loadBatch(loadedNestedTracks))
        state = getState()
      }

      mainTrackIds.forEach(id => {
        const parentTrack = getTrack(state, {
          id,
        })

        const nestedTracks = (parentTrack?.nested_tracks ?? [])
          .map(({ id }) =>
            getTrack(state, {
              id,
            }),
          )
          .filter(track => track !== null && track !== undefined)

        mapResponseToReducerObjects(dispatch, getState, {
          ...parentTrack,
          nested_tracks: nestedTracks,
        })

        if (nestedTracks.length !== parentTrack?.nested_tracks?.length) {
          throw new Error('Failed to load tracks')
        }

        const nested_ids = nestedTracks.map(({ id }) => id)
        dispatch({
          type: NESTING_TRACKS_LOAD_SUCCESS,
          id,
          nested_ids,
        })
      })
    } catch (error) {
      dispatch(sendGenericError(error))
      mainTrackIds.forEach(id => {
        dispatch({
          type: NESTING_LOAD_ERROR,
          id,
          error,
        })
      })
    }
  }
}
/**
 * Updates nested tracks with new field values and syncs the state.
 * @param itemsWithUpdatedFields - Map of track IDs to their updated field values
 * @returns Thunk that updates tracks and normalizes shortlists
 */
export function updateNestedTracks(
  itemsWithUpdatedFields: Record<TrackId, ItemWithUpdatedFields>,
): Thunk {
  return async dispatch => {
    await api.tracksBatchUpdate(itemsWithUpdatedFields)
    const affectedTrackIds = Object.keys(itemsWithUpdatedFields).map(Number)
    dispatch(syncMainTrackAndPane(affectedTrackIds, itemsWithUpdatedFields))
    dispatch(normalizeNestedTracksInShortlists())
  }
}
// updateTracksAndShiftPosition works with already nested tracks in redux state
// utils/nesting#updateTrackAndShiftPosition works with useState
/**
 * Updates track nesting relationships and shifts positions accordingly.
 * @param mainTrack - The main track that will contain the nested tracks
 * @param trackIds - Array of track IDs to nest under the main track
 * @param nestingLabel - The nesting label (version or stem)
 * @param position - The position to insert the tracks at
 * @returns Thunk that calculates updated field values for position shifting
 */
export function updateTracksAndShiftPosition(
  mainTrack: Track,
  trackIds: TrackId[],
  nestingLabel: NestingLabel,
  position: number,
): Thunk {
  return async (_dispatch, getState) => {
    const state = getState()
    const filteredTracks: Track[] = trackIds
      .map(id =>
        getTrack(state, {
          id,
        }),
      )
      .filter(Boolean)
      .map((track: Track) => ({
        ...track,
        nesting_parent_id: mainTrack.id,
        nesting_label: nestingLabel,
      }))
    const nestedTracks: Track[] = getTracksByNestingParentId(
      state,
      mainTrack,
    ).filter(({ id }) => !trackIds.includes(id))
    let currentCategory = getVersionsFromTracksArray(nestedTracks)
    let oppositeCategory = getStemsFromTracksArray(nestedTracks)

    if (nestingLabel === STEM) {
      currentCategory = getStemsFromTracksArray(nestedTracks)
      oppositeCategory = getVersionsFromTracksArray(nestedTracks)
    }

    let itemsWithUpdatedFields = shiftPosition(
      addItems(currentCategory, position, filteredTracks),
    )
    itemsWithUpdatedFields = {
      ...itemsWithUpdatedFields,
      ...shiftPosition(oppositeCategory),
    }
    return itemsWithUpdatedFields
  }
}
/**
 * Adds tracks as nested tracks to a main track with validation and quota checks.
 * @param targetTrack - The target track to nest tracks under
 * @param tracks - Array of tracks to nest
 * @param nestingLabel - The nesting label (version or stem)
 * @param position - The position to insert tracks at (default: 0)
 * @returns Thunk that validates and adds nested tracks with proper error handling
 */
export function addNestedTracksToMain(
  targetTrack: Track,
  tracks: Track[],
  nestingLabel: NestingLabel,
  position = 0,
): Thunk {
  return async dispatch => {
    let mainTrack = targetTrack

    if (targetTrack.nesting_parent_id) {
      try {
        mainTrack = await dispatch(
          getOrFetchTrack(targetTrack.nesting_parent_id),
        )
      } catch (_e) {
        pushUINotification({
          level: 'error',
          message: `Unable to fetch parent track!`,
        })
      }
    }

    const audioFiles = tracks.filter(
      t => getFileTypeByExt(t.ext) === AUDIO_FILE && Boolean(t.id),
    )
    const trackIds = audioFiles.map(t => t.id)
    const trackString = plural(trackIds.length, 'track', 'tracks')
    if (trackIds.length === 0) return

    if (trackIds.includes(mainTrack.id)) {
      NavigationService.push(
        `/explorer/tracks/${mainTrack.id}/nesting-validation`,
        {
          state: {
            type: NESTED_INTO_ITSELF,
          },
        },
      )
      return
    }

    const trackWithNestedTracks = audioFiles.find(
      track => getNestedTracksCount(track) > 0,
    )

    if (trackWithNestedTracks) {
      NavigationService.push(
        `/explorer/tracks/${mainTrack.id}/nesting-validation`,
        {
          state: {
            type: NESTED_TRACK_INTO_ANOTHER_TRACK,
            track: trackWithNestedTracks.id,
            newVersionOf: mainTrack.id,
            nestedTracks: audioFiles,
            nestingLabel: nestingLabel,
          },
        },
      )
      return
    }

    const alreadyNestedTrack = audioFiles.find(
      track =>
        track.nesting_parent_id !== null &&
        track.nesting_parent_id !== mainTrack.id,
    )

    if (alreadyNestedTrack) {
      NavigationService.push(
        `/explorer/tracks/${mainTrack.id}/nesting-validation`,
        {
          state: {
            nestedTracks: audioFiles,
            nestingLabel,
            newVersionOf: mainTrack.id,
            track: alreadyNestedTrack.id,
            type: ALREADY_NESTED_TRACK,
            versionOf: alreadyNestedTrack.nesting_parent_id,
          },
        },
      )
      return
    }

    const newAudioFiles = audioFiles.filter(
      item => item.nesting_parent_id !== mainTrack.id,
    )
    const isNestingOverQuota = checkNestedTracksQuota(mainTrack, newAudioFiles)

    if (isNestingOverQuota && Boolean(newAudioFiles.length)) {
      NavigationService.push(
        `/explorer/tracks/${mainTrack.id}/nesting-validation`,
        {
          state: {
            type: TOO_MANY_VERSIONS,
          },
        },
      )
      return
    }

    try {
      const itemsWithUpdatedFields = await dispatch(
        updateTracksAndShiftPosition(
          mainTrack,
          trackIds,
          nestingLabel,
          position,
        ),
      )
      dispatch({
        type: NESTING_UPDATING,
        id: mainTrack.id,
      })
      await api.tracksBatchUpdate(itemsWithUpdatedFields)
      dispatch(syncMainTrackAndPane([mainTrack.id], itemsWithUpdatedFields))
      // TODO: update notification if needed
      pushUINotification({
        level: 'success',
        message: `${trackString} nested!`,
        action: {
          label: 'Manage nesting',
          link: `/explorer/tracks/${mainTrack.id}/manage-nesting`,
        },
      })
      dispatch({
        type: NESTING_UPDATING_SUCCESS,
        id: mainTrack.id,
      })
      dispatch(normalizeNestedTracksInShortlists())
    } catch (error) {
      pushUINotification({
        message: `Failed to nest ${trackString}.`,
        level: 'error',
      })
      dispatch(sendGenericError(error))
      dispatch({
        type: NESTING_UPDATING_ERROR,
        id: mainTrack.id,
        error,
      })
    }
  }
}
/**
 * Synchronizes main track and explorer pane data after nesting changes.
 * @param trackIds - Array of track IDs that were affected
 * @param itemsWithUpdatedFields - Map of track IDs to their updated field values
 * @returns Thunk that updates tracks and panes with correct nesting information
 */
export function syncMainTrackAndPane(
  trackIds: TrackId[],
  itemsWithUpdatedFields: Record<TrackId, ItemWithUpdatedFields>,
): Thunk {
  return (dispatch, getState) => {
    let affectedMainTrackIds: number[] = []
    // save track changes in state to get correct nestedTracks and show correct tracks labels
    const tracksWithNewFields = Object.keys(itemsWithUpdatedFields).map(id => {
      const state = getState()
      const trackId = Number(id)
      const track = getTrack(state, {
        id: trackId,
      })

      if (
        track &&
        track.nesting_parent_id &&
        track.nesting_parent_id !==
          itemsWithUpdatedFields?.[trackId]?.nesting_parent_id
      ) {
        affectedMainTrackIds.push(track.nesting_parent_id)
      }

      return { ...track, ...itemsWithUpdatedFields[trackId] }
    })
    affectedMainTrackIds = affectedMainTrackIds.filter(Boolean)
    dispatch(loadBatch(tracksWithNewFields))
    const updatedPanes: ExplorerPane[] = []
    const updatedTracks: Track[] = []
    const affectedTrackIds = affectedMainTrackIds
      .concat(trackIds)
      .filter(Boolean)
    affectedTrackIds.forEach(trackId => {
      const state = getState()
      const track = state.tracks[trackId]
      const trackPanes = getExplorerPanesByTrackId(state, track.id)
      const nestedTracks: Track[] = getTracksByNestingParentId(state, track)
      const versions = getVersionsFromTracksArray(nestedTracks)
      const stems = getStemsFromTracksArray(nestedTracks)
      const updatedFields = {
        nested_stem_count: stems.length,
        nested_version_count: versions.length,
        nesting_label: track.nesting_label,
        nesting_parent_id: track.nesting_parent_id,
        nesting_position: track.nesting_position,
        nested_tracks: nestedTracks,
      }
      updatedTracks.push({ ...track, ...updatedFields })

      // update pane
      if (trackPanes.length) {
        trackPanes.forEach(trackPane => {
          updatedPanes.push({ ...trackPane, ...updatedFields })
        })
      }
    })
    // save correct nesting fields in panes/tracks
    dispatch(loadBatch(updatedTracks))
    dispatch(updatePanes(updatedPanes))
  }
}
/**
 * Checks if adding tracks would exceed the nesting quota limits.
 * @param mainTrack - The main track to check quota for
 * @param tracksForNesting - Array of tracks to be nested
 * @returns True if adding tracks would exceed quota, false otherwise
 */
export function checkNestedTracksQuota(
  mainTrack: Track,
  tracksForNesting: Track[],
): boolean {
  const versionsCount = getVersionsFromTracksArray(tracksForNesting).length
  const stemsCount = getStemsFromTracksArray(tracksForNesting).length
  const nestedVersionCount = getVersionsCountFromTracksArray(tracksForNesting)
  const nestedStemsCount = getStemsCountFromTracksArray(tracksForNesting)
  const versionsOverQuota =
    getVersionsCount(mainTrack) + versionsCount + nestedVersionCount >
    VERSIONS_LIMIT
  const stemsOverQuota =
    getStemsCount(mainTrack) + stemsCount + nestedStemsCount > STEMS_LIMIT
  return versionsOverQuota || stemsOverQuota
}
/**
 * Prepares nested tracks for nesting into another track by resetting counts.
 * @param mainTrack - The main track being processed
 * @param tracks - Array of tracks to prepare for re-nesting
 * @returns Array of tracks prepared for nesting into another track
 */
export function getNestedTracksForNestingIntoAnother(
  mainTrack: Track,
  tracks: Track[],
): Track[] {
  const updatedNestedTracks = tracks.filter(item => item.id !== mainTrack.id)
  const updatedTrack = {
    ...mainTrack,
    nested_stem_count: 0,
    nested_version_count: 0,
  }
  const updatedTracks = [...updatedNestedTracks, updatedTrack]
  return updatedTracks
}
